import React, { useState, useEffect } from 'react';
import { useToast } from '../../Toast';
import payerDatabase from '../../../data/payerDatabase.json';

interface BillingProfile {
  id: string;
  rule: {
    clinicianId?: string;
    clinicianName?: string;
    payerId?: string;
    payerName?: string;
  };
  providerType: 'individual' | 'organization';
  firstName?: string;
  middleName?: string;
  lastName?: string;
  organizationName?: string;
  taxIdOrSSN: string;
  npi: string;
  taxonomyCode: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

interface Props {
  profile: BillingProfile | null;
  onSave: (profile: BillingProfile) => void;
  onClose: () => void;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const BillingProfileModal: React.FC<Props> = ({ profile, onSave, onClose }) => {
  const toast = useToast();
  // Rule fields
  const [clinicianSearch, setClinicianSearch] = useState('');
  const [selectedClinician, setSelectedClinician] = useState<{ id: string; name: string } | null>(null);
  const [payerSearch, setPayerSearch] = useState('');
  const [selectedPayer, setSelectedPayer] = useState<{ id: string; name: string } | null>(null);
  const [showPayerDropdown, setShowPayerDropdown] = useState(false);

  // Provider info fields
  const [providerType, setProviderType] = useState<'individual' | 'organization'>('individual');
  const [useDefaultInfo, setUseDefaultInfo] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [taxIdOrSSN, setTaxIdOrSSN] = useState('');
  const [npi, setNPI] = useState('');
  const [taxonomyCode, setTaxonomyCode] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');

  // Load profile data if editing
  useEffect(() => {
    if (profile) {
      if (profile.rule.clinicianId && profile.rule.clinicianName) {
        setSelectedClinician({ id: profile.rule.clinicianId, name: profile.rule.clinicianName });
      }
      if (profile.rule.payerId && profile.rule.payerName) {
        setSelectedPayer({ id: profile.rule.payerId, name: profile.rule.payerName });
      }
      setProviderType(profile.providerType);
      setFirstName(profile.firstName || '');
      setMiddleName(profile.middleName || '');
      setLastName(profile.lastName || '');
      setOrganizationName(profile.organizationName || '');
      setTaxIdOrSSN(profile.taxIdOrSSN);
      setNPI(profile.npi);
      setTaxonomyCode(profile.taxonomyCode);
      setStreet(profile.address.street);
      setCity(profile.address.city);
      setState(profile.address.state);
      setZipCode(profile.address.zipCode);
    }
  }, [profile]);

  // Filter payers based on search
  const filteredPayers = payerDatabase.filter(payer =>
    payer.payerName.toLowerCase().includes(payerSearch.toLowerCase()) ||
    payer.payerId.toLowerCase().includes(payerSearch.toLowerCase())
  );

  const handleSave = () => {
    // Validate required fields
    if (providerType === 'individual' && (!firstName || !lastName)) {
      toast.warning('First name and last name are required for individual provider type');
      return;
    }
    if (providerType === 'organization' && !organizationName) {
      toast.warning('Organization name is required for organization provider type');
      return;
    }
    if (!taxIdOrSSN || !npi || !taxonomyCode || !street || !city || !state || !zipCode) {
      toast.warning('Please fill in all required fields');
      return;
    }

    const newProfile: BillingProfile = {
      id: profile?.id || '',
      rule: {
        clinicianId: selectedClinician?.id,
        clinicianName: selectedClinician?.name,
        payerId: selectedPayer?.id,
        payerName: selectedPayer?.name
      },
      providerType,
      firstName: providerType === 'individual' ? firstName : undefined,
      middleName: providerType === 'individual' ? middleName : undefined,
      lastName: providerType === 'individual' ? lastName : undefined,
      organizationName: providerType === 'organization' ? organizationName : undefined,
      taxIdOrSSN,
      npi,
      taxonomyCode,
      address: {
        street,
        city,
        state,
        zipCode
      }
    };

    onSave(newProfile);
  };

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '40px',
        zIndex: 2000,
        overflow: 'auto'
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-profile-title"
        aria-describedby="billing-profile-description"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#0A2540',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #6CA6C1',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: '#0A2540',
          zIndex: 1
        }}>
          <h2 id="billing-profile-title" style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF', margin: 0 }}>
            Billing provider info
          </h2>
          <button
            onClick={onClose}
            aria-label="Close billing profile dialog"
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#87CEEB',
              fontSize: '24px',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          <p id="billing-profile-description" style={{ fontSize: '14px', color: '#E0E0E0', marginBottom: '24px' }}>
            Configure billing provider information and rules for insurance claims
          </p>
          {/* Set the rule section */}
          <section style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px' }}>
              *Set the rule for when to use this profile
            </h3>

            {/* Clinician search */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                Clinician
              </label>
              <input
                type="text"
                value={selectedClinician?.name || clinicianSearch}
                onChange={(e) => setClinicianSearch(e.target.value)}
                placeholder="Search clinicians"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#87CEEB', marginTop: '4px' }}>
                Leave blank to apply to all clinicians
              </p>
            </div>

            {/* Insurance payer dropdown */}
            <div style={{ marginBottom: '16px', position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                Insurance payer
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={selectedPayer?.name || payerSearch}
                  onChange={(e) => {
                    setPayerSearch(e.target.value);
                    setShowPayerDropdown(true);
                  }}
                  onFocus={() => setShowPayerDropdown(true)}
                  placeholder="Search my payers"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#0F4C75',
                    border: '1px solid #6CA6C1',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                />
                <span style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#87CEEB',
                  pointerEvents: 'none'
                }}>
                  ▼
                </span>
              </div>

              {showPayerDropdown && filteredPayers.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <div
                    onClick={() => {
                      setSelectedPayer(null);
                      setPayerSearch('');
                      setShowPayerDropdown(false);
                    }}
                    style={{
                      padding: '10px 12px',
                      fontSize: '14px',
                      color: '#1E90FF',
                      cursor: 'pointer',
                      borderBottom: '1px solid #6CA6C1'
                    }}
                  >
                    All insurance payers
                  </div>
                  {filteredPayers.map((payer) => (
                    <div
                      key={payer.payerId}
                      onClick={() => {
                        setSelectedPayer({ id: payer.payerId, name: `${payer.payerName} (${payer.payerId})` });
                        setPayerSearch('');
                        setShowPayerDropdown(false);
                      }}
                      style={{
                        padding: '10px 12px',
                        fontSize: '14px',
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        borderBottom: '1px solid #2A5470'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#1E4A6A';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {payer.payerName} ({payer.payerId})
                    </div>
                  ))}
                </div>
              )}

              <p style={{ fontSize: '12px', color: '#87CEEB', marginTop: '4px' }}>
                This will apply to all plans with the same payer id
              </p>
            </div>
          </section>

          {/* Use the following billing info section */}
          <section style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px' }}>
              Use the following billing info
            </h3>

            {/* Provider type radio */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                Provider type
              </label>
              <div style={{ display: 'flex', gap: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={providerType === 'individual'}
                    onChange={() => setProviderType('individual')}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Individual</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={providerType === 'organization'}
                    onChange={() => setProviderType('organization')}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Organization</span>
                </label>
              </div>
            </div>

            {/* Use default information toggle */}
            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                <input
                  type="checkbox"
                  checked={useDefaultInfo}
                  onChange={(e) => setUseDefaultInfo(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: useDefaultInfo ? '#1E90FF' : '#6CA6C1',
                  borderRadius: '24px',
                  transition: '0.3s'
                }}>
                  <span style={{
                    position: 'absolute',
                    height: '18px',
                    width: '18px',
                    left: useDefaultInfo ? '23px' : '3px',
                    bottom: '3px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '50%',
                    transition: '0.3s'
                  }} />
                </span>
              </label>
              <span style={{ fontSize: '14px', color: '#E0E0E0' }}>
                Use default information from practice settings
              </span>
            </div>

            {/* Name fields for Individual */}
            {providerType === 'individual' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                    * First name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: '#0F4C75',
                      border: '1px solid #6CA6C1',
                      borderRadius: '6px',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                    Middle name
                  </label>
                  <input
                    type="text"
                    value={middleName}
                    onChange={(e) => setMiddleName(e.target.value)}
                    placeholder="Middle name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: '#0F4C75',
                      border: '1px solid #6CA6C1',
                      borderRadius: '6px',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                    * Last name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: '#0F4C75',
                      border: '1px solid #6CA6C1',
                      borderRadius: '6px',
                      color: '#FFFFFF',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Organization name for Organization */}
            {providerType === 'organization' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                  * Organization name
                </label>
                <input
                  type="text"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="Organization name"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#0F4C75',
                    border: '1px solid #6CA6C1',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                />
              </div>
            )}

            {/* Tax ID or SSN */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                * Tax ID or SSN (Box 25)
                <span style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}>ⓘ</span>
              </label>
              <input
                type="text"
                value={taxIdOrSSN}
                onChange={(e) => setTaxIdOrSSN(e.target.value)}
                placeholder="XX-XXXXXXX"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* NPI */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                * NPI (Box 33a)
              </label>
              <input
                type="text"
                value={npi}
                onChange={(e) => setNPI(e.target.value)}
                placeholder="0000000000"
                maxLength={10}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Taxonomy code */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                * Taxonomy code (Box 33b)
              </label>
              <input
                type="text"
                value={taxonomyCode}
                onChange={(e) => setTaxonomyCode(e.target.value)}
                placeholder="0000000000"
                maxLength={10}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Address section */}
            <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginTop: '24px', marginBottom: '16px' }}>
              Address
            </h4>

            {/* Street */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                * Street
              </label>
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="Start typing a street address"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* City, State, ZIP */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                  * City
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#0F4C75',
                    border: '1px solid #6CA6C1',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                  * State
                </label>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#0F4C75',
                    border: '1px solid #6CA6C1',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select</option>
                  {US_STATES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                  * ZIP
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="ZIP"
                  maxLength={10}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#0F4C75',
                    border: '1px solid #6CA6C1',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 24px',
          borderTop: '1px solid #6CA6C1',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#0A2540'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              backgroundColor: 'transparent',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '10px 24px',
              backgroundColor: '#1E90FF',
              border: 'none',
              borderRadius: '6px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Save profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingProfileModal;
